import {Injectable} from '@angular/core';
import {Questionnaire} from '@ngmedax/common-questionnaire-types';
import {ValueService} from '@ngmedax/value';
import {tokenizer} from 'acorn';
import {formulaPath, formulaRejectedCall, formulaSource, parseFormula, visitFormula} from '../components/formula-editor/formula-language';
import {compileFormulas, formulaReferenceRange} from './formula-compiler';

/**
 * Position of the offending part inside the raw expression (as typed in the editor)
 */
export interface FormulaErrorRange {
  start: number;
  end: number;
}

/**
 * Structured validation error for a formula expression
 */
export interface FormulaValidationError {
  type: 'syntax' | 'unknownVariable' | 'unknownMathFunction' | 'invalidFunctionCall' | 'quotes' | 'circular'
    | 'formulaNotInlinable' | 'formulaBrokenDependency';
  detail?: string;
  /**
   * Detail code for well known syntax problems, so the ui can show a translated message
   */
  detailCode?: 'missingClosingParen' | 'extraClosingParen';
  /**
   * Where the offending token sits in the raw expression. Null when unknown
   */
  range?: FormulaErrorRange | null;
}

/**
 * Validates formula expressions in the editor the same way the app executes them.
 *
 * The app (mobile app: services/questionnaire/formula.ts) executes formulas via
 * vm.runInNewContext with the questionnaire variables object as context. This
 * service mirrors the relevant behavior without executing anything:
 *
 * - source is stored without changing its expression or statement semantics
 * - same "(if" unwrapping before execution
 * - same function call allowlist (if statements, Math functions, .match method)
 * - syntax check with a JavaScript parser, never running the statement
 * - referenced variables are checked against the variables known to the questionnaire
 *
 * Additionally provides a conservative autofix for mechanical problems
 * (unbalanced parentheses, "else (x)" instead of "else x").
 */
@Injectable()
export class FormulaValidationService {
  /**
   * Variable scopes that exist at fill time as part of the formula execution context.
   * See mobile app: services/questionnaire/variable.ts (getInitialVariables)
   */
  private static readonly RUNTIME_SCOPES = ['inline', 'extern', 'formula', 'scoring', 'container', 'client', 'patient'];

  /**
   * Scopes whose variable names are fully known in the editor and can be checked strictly
   */
  private static readonly STRICT_SCOPES = ['inline', 'extern', 'scoring'];

  /**
   * Injects dependencies
   */
  public constructor(private value: ValueService) {
  }

  /**
   * Validates given formula expression. Returns null when valid.
   *
   * @param {string} rawExpression expression as currently typed in the editor (before save wrapping)
   * @param {Questionnaire} questionnaire questionnaire to resolve known variables from
   * @returns {FormulaValidationError | null}
   */
  public validate(rawExpression: string, questionnaire: Questionnaire): FormulaValidationError | null {
    const expression = (rawExpression || '').trim();

    // legacy empty formulas may be stored as "()"
    if (!expression || expression.match(/^\(\s*\)$/)) {
      return null;
    }

    const stmt = this.toRuntimeStatement(expression);

    const functionCallError = this.checkFunctionCalls(stmt, rawExpression);
    if (functionCallError) {
      return functionCallError;
    }

    const syntaxError = this.checkSyntax(rawExpression);
    if (syntaxError) {
      return syntaxError;
    }

    try { return this.checkVariableReferences(rawExpression, questionnaire); }
    catch (error) {
      console.warn('Unable to inspect formula references; preserving the source for manual editing.', error);
      return {type: 'syntax', detail: 'Unable to inspect formula references'};
    }
  }
  /**
   * Checks formulas that use other formulas over all formulas of the questionnaire: formulas that use each other,
   * formulas that use a broken formula and formulas that can not be used inside another formula.
   * Single formula problems (syntax, unknown variables, …) are left to validate().
   *
   * @param {Questionnaire} questionnaire
   * @param {{[name: string]: string}} drafts not yet saved sources, they replace the saved ones
   * @returns {{[name: string]: FormulaValidationError}}
   */
  public validateFormulaUsage(questionnaire: Questionnaire, drafts: {[name: string]: string} = {}): {[name: string]: FormulaValidationError} {
    const formulas = this.value.get(questionnaire, ['meta', 'options', 'variables', 'formula']) || {};
    const sources: {[name: string]: string} = {};

    Object.keys(formulas).forEach(name => {
      const source = Object.prototype.hasOwnProperty.call(drafts, name) ? drafts[name] : (formulas[name] || {}).expression;
      sources[name] = source === undefined || source === null ? '' : source + '';
    });

    const results = compileFormulas(sources);
    const errors: {[name: string]: FormulaValidationError} = {};

    Object.keys(results).forEach(name => {
      const error = results[name].error;

      if (!error || error.type === 'syntax' || error.type === 'unknownFormula') {
        return;
      }

      // mark the reference that causes the problem
      const culprit = error.type === 'circular' ? error.cycle[error.cycle.indexOf(name) + 1] : error.detail;
      const range = formulaReferenceRange(sources[name], culprit);

      if (error.type === 'circular') {
        errors[name] = {type: 'circular', detail: error.cycle.join(' → '), range};
      } else if (error.type === 'brokenDependency') {
        errors[name] = {type: 'formulaBrokenDependency', detail: error.detail, range};
      } else if (error.type === 'notInlinable') {
        errors[name] = {type: 'formulaNotInlinable', detail: error.detail, range};
      }
    });

    return errors;
  }


  /**
   * Tries to mechanically fix given broken expression. Only fixes problems where the
   * intent is unambiguous: unbalanced parentheses and "else (x)" (which the app
   * rejects at runtime, only "else x" is valid). Returns the fixed expression when
   * the result validates cleanly, null otherwise.
   *
   * @param {string} rawExpression
   * @param {Questionnaire} questionnaire
   * @returns {string | null}
   */
  public autofix(rawExpression: string, questionnaire: Questionnaire): string | null {
    let expr = (rawExpression || '').trim();

    if (!this.validate(expr, questionnaire)) {
      return null;
    }

    // each pass fixes at most one problem, so allow a few passes
    for (let i = 0; i < 10; i++) {
      const error = this.validate(expr, questionnaire);

      if (!error) {
        break;
      }

      const fixed = this.applyFix(expr, error);

      if (fixed === null || fixed === expr) {
        break;
      }

      expr = fixed;
    }

    return this.validate(expr, questionnaire) ? null : expr;
  }

  /**
   * Applies a single mechanical fix for given error. Returns null when no fix is known
   *
   * @param {string} expr
   * @param {FormulaValidationError} error
   * @returns {string | null}
   */
  private applyFix(expr: string, error: FormulaValidationError): string | null {
    if (error.type === 'syntax') {
      const scan = this.scanParens(this.stripRegexLiterals(expr));

      if (scan && scan.kind === 'missing') {
        return expr + ')';
      }

      if (scan && scan.kind === 'extra') {
        return expr.slice(0, scan.index) + expr.slice(scan.index + 1);
      }
    }

    if (error.type === 'invalidFunctionCall' && error.detail === 'else') {
      try {
        const offset = formulaSource(expr).offset;
        let fixed: string = null;
        visitFormula(parseFormula(expr), node => {
          if (fixed || node.type !== 'IfStatement' || node.alternate?.type !== 'ExpressionStatement') { return; }
          const start = node.alternate.start + offset;
          const end = node.alternate.end + offset;
          const wrapped = expr.slice(start, end).match(/^\(\s*([^(){}]*?)\s*\)(;?)$/);
          if (wrapped) { fixed = expr.slice(0, start) + wrapped[1] + wrapped[2] + expr.slice(end); }
        });
        return fixed;
      } catch (_) { return null; }
    }

    return null;
  }

  /**
   * Unwraps legacy formulas exactly as the filling application does before execution
   *
   * @param {string} expression
   * @returns {string}
   */
  private toRuntimeStatement(expression: string): string {
    return formulaSource(expression).code;
  }

  /**
   * Checks function calls against the same allowlist the app enforces at execution time.
   * Additionally verifies that referenced Math functions actually exist.
   *
   * @param {string} stmt
   * @param {string} rawExpression
   * @returns {FormulaValidationError | null}
   */
  private checkFunctionCalls(stmt: string, rawExpression: string): FormulaValidationError | null {
    const functionCall = formulaRejectedCall(stmt);
    if (functionCall) {
      const token = functionCall.replace(/\($/, '').trim();
      return {type: 'invalidFunctionCall', detail: token, range: this.rangeOfToken(rawExpression, token)};
    }


    return null;
  }

  /**
   * Parses the statement without executing it and preserves precise syntax error positions
   *
   * @param {string} stmt
   * @param {string} rawExpression
   * @returns {FormulaValidationError | null}
   */
  private checkSyntax(rawExpression: string): FormulaValidationError | null {
    try {
      parseFormula(rawExpression);
    } catch (error) {
      const syntax = error as SyntaxError & {pos?: number};
      const offset = formulaSource(rawExpression).offset;
      const scan = this.scanParens(this.stripRegexLiterals(rawExpression.trim()));
      return {type: 'syntax', detail: syntax.message,
        detailCode: scan ? scan.kind === 'missing' ? 'missingClosingParen' : 'extraClosingParen' : undefined,
        range: this.rangeAt(rawExpression, (syntax.pos || 0) + offset, 1)};
    }
    return null;
  }

  /**
   * Checks referenced variables (scope.name) against the variables known to the questionnaire.
   * Strict for questionnaire defined scopes (inline, extern, scoring). References to scopes
   * that do not exist in the formula execution context at all are reported as unknown.
   * Properties of client/patient/container are not checked (not fully known in the editor).
   *
   * @param {string} stmt
   * @param {string} rawExpression
   * @param {Questionnaire} questionnaire
   * @returns {FormulaValidationError | null}
   */
  private checkVariableReferences(
    rawExpression: string, questionnaire: Questionnaire
  ): FormulaValidationError | null {
    const variables = this.value.get(questionnaire, ['meta', 'options', 'variables']) || {};
    const tree = parseFormula(rawExpression);
    const offset = formulaSource(rawExpression).offset;
    const locals = new Set<string>();
    const builtins = ['Math', 'Number', 'String', 'Boolean', 'Array', 'Object', 'RegExp', 'Date', 'JSON'];
    visitFormula(tree, node => {
      if (node.type === 'VariableDeclarator') {
        visitFormula(node.id, binding => { if (binding.type === 'Identifier') { locals.add(binding.name); } });
      }
    });
    let error: FormulaValidationError = null;
    visitFormula(tree, (node, parent) => {
      if (error) { return; }
      const reference = formulaPath(node);
      if (node.type === 'CallExpression') {
        const path = formulaPath(node.callee);
        if (path.startsWith('Math.') && typeof Math[path.slice(5)] !== 'function') {
          error = {type: 'unknownMathFunction', detail: path,
            range: {start: node.callee.start + offset, end: node.callee.end + offset}};
        }
      }
      if (node.type !== 'MemberExpression' || !reference ||
        (parent?.type === 'MemberExpression' && parent.object === node)) { return; }
      const [scope, varName] = reference.split('.');
      // formulas may use other formulas, the editor resolves them for the app (see formula-compiler.ts).
      // Only the name is checked here, cycles and unusable formulas are checked over all formulas
      if (scope === 'formula') {
        !Object.prototype.hasOwnProperty.call(variables['formula'] || {}, varName) && (error = {type: 'unknownVariable',
          detail: reference, range: {start: node.start + offset, end: node.end + offset}});
        return;
      }
      if (locals.has(scope) || builtins.indexOf(scope) !== -1) { return; }
      if (FormulaValidationService.RUNTIME_SCOPES.indexOf(scope) === -1 ||
        (FormulaValidationService.STRICT_SCOPES.indexOf(scope) !== -1 &&
          !Object.prototype.hasOwnProperty.call(variables[scope] || {}, varName))) {
        error = {type: 'unknownVariable', detail: reference,
          range: {start: node.start + offset, end: node.end + offset}};
      }
    });
    return error;
  }

  /**
   * Masks string and regular expression literals before mechanical parenthesis repair
   *
   * @param {string} stmt
   * @returns {string}
   */
  private stripRegexLiterals(stmt: string): string {
    const chars = stmt.split('');
    try {
      for (const token of tokenizer(stmt, {ecmaVersion: 'latest', onComment: (_block, _text, start, end) => {
        for (let index = start; index < end; index++) { chars[index] = ' '; }
      }})) {
        if (['regexp', 'string', 'template'].indexOf(token.type.label) !== -1) {
          for (let index = token.start; index < token.end; index++) { chars[index] = ' '; }
        }
      }
    } catch (_) {
      // retain scanned literal ranges even when the remaining expression is incomplete
    }
    return chars.join('');
  }

  /**
   * Scans for the first unbalanced parenthesis. Returns its position and whether a
   * closing paren is missing (unclosed "(") or extra (unmatched ")"). Null when balanced
   *
   * @param {string} expr
   * @returns {{kind: 'missing' | 'extra', index: number} | null}
   */
  private scanParens(expr: string): {kind: 'missing' | 'extra', index: number} | null {
    const openStack: number[] = [];

    for (let i = 0; i < expr.length; i++) {
      if (expr[i] === '(') {
        openStack.push(i);
      }

      if (expr[i] === ')') {
        if (!openStack.length) {
          return {kind: 'extra', index: i};
        }
        openStack.pop();
      }
    }

    return openStack.length ? {kind: 'missing', index: openStack[0]} : null;
  }

  /**
   * Returns the range of the first occurrence of given token in the raw expression
   *
   * @param {string} rawExpression
   * @param {string} token
   * @returns {FormulaErrorRange | null}
   */
  private rangeOfToken(rawExpression: string, token: string): FormulaErrorRange | null {
    const index = (rawExpression || '').indexOf(token);
    return index === -1 ? null : {start: index, end: index + token.length};
  }

  /**
   * Returns a range at given index with given length, when the index is valid
   *
   * @param {string} rawExpression
   * @param {number} index
   * @param {number} length
   * @returns {FormulaErrorRange | null}
   */
  private rangeAt(rawExpression: string, index: number, length: number): FormulaErrorRange | null {
    return (typeof index === 'number' && index >= 0 && index < (rawExpression || '').length)
      ? {start: index, end: index + length}
      : null;
  }
}
